class CartDeliverySummary extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('[data-cds-inner]');
    this.update();
    document.addEventListener('pss:coords-changed', () => this.update());
  }

  async update() {
    const postcode = sessionStorage.getItem('customerPostcode');
    if (!postcode) {
      this.hidden = true;
      return;
    }

    const country = sessionStorage.getItem('customerCountry') || 'NZ';
    const label = sessionStorage.getItem('customerPostcodeLabel') || postcode;
    const coords = JSON.parse(sessionStorage.getItem('customerCoords') || 'null');
    const variantId = this.dataset.variantId;

    this.hidden = false;
    this.inner.innerHTML = '<div class="cds__loading" aria-live="polite" aria-label="Loading delivery options"></div>';

    const [bespokeRates, storeAvailability] = await Promise.all([
      this.fetchBespokeRates(postcode, country),
      variantId ? this.fetchStoreAvailability(variantId) : Promise.resolve([]),
    ]);

    const nearestStore = this.findNearestStore(storeAvailability, coords);
    this.render(label, bespokeRates, nearestStore, country, coords);
  }

  render(label, bespokeRates, nearestStore, country, coords) {
    const isNZ = country === 'NZ';
    const nearestDist = nearestStore?.distance ?? null;
    const nzHour = this.getNZHour();

    const uberCutoff = window.pss?.uberCutoffHour ?? 14;
    const ccCutoff = window.pss?.ccCutoffHour ?? 16;
    const uberDistKm = window.pss?.uberDistanceKm ?? 10;
    const ccDistKm = window.pss?.ccDistanceKm ?? 30;

    const showUber = isNZ && nzHour < uberCutoff && nearestStore !== null && (nearestDist === null || nearestDist <= uberDistKm);
    const showCC = isNZ && nearestStore !== null && (nearestDist === null || nearestDist <= ccDistKm);
    const afterCCCutoff = isNZ && nzHour >= ccCutoff;

    const moneyFormat = window.pss?.moneyFormat || '${{amount}}';

    // Island-aware messaging: look up the configured delivery timeframe for the
    // customer's locale ('NI'/'SI' for NZ, 'AU' for Australia). Set on the PDP
    // when a postcode is chosen; fall back to the NZ default when absent.
    const configLocale = sessionStorage.getItem('customerConfigLocale') || (isNZ ? 'NI' : country);
    const themeOptions = window.pss?.deliveryOptions || [];
    const etaFor = (serviceName) => {
      if (!serviceName) return null;
      const opt = themeOptions.find((o) =>
        serviceName.trim().toLowerCase().includes(o.name.trim().toLowerCase())
      );
      return opt?.eta?.[configLocale] || null;
    };

    // Identify same-day/Uber rates and standard rates from Bespoke response
    const sameDayRate = bespokeRates.find((r) => this.isSameDayRate(r.service_name));
    const standardRate = bespokeRates.find((r) => !this.isSameDayRate(r.service_name));

    let items = '';

    if (showUber && sameDayRate) {
      const price = this.formatMoney(sameDayRate.total_price, moneyFormat, sameDayRate.currency);
      items += this.renderItem('Same Day', price, sameDayRate.service_name, 'Fastest');
    }

    if (standardRate) {
      const price = this.formatMoney(standardRate.total_price, moneyFormat, standardRate.currency);
      items += this.renderItem(standardRate.service_name, price, etaFor(standardRate.service_name), null);
    }

    // if (showCC) {
    //   const ccBadge = afterCCCutoff ? 'Collect Tomorrow' : 'Ready in 2 Hours';
    //   items += this.renderItem('Click & Collect', 'FREE', null, ccBadge);
    // }

    if (!items) {
      this.hidden = true;
      return;
    }

    // const storeRow = (showCC && nearestStore) ? this.renderStoreRow(nearestStore) : '';

    this.inner.innerHTML = `
      <div class="cds__header">
        <span class="cds__location">${this.escapeHtml(label)}</span>
        <button type="button" class="cds__change" data-cds-change>Change</button>
      </div>
      <ul class="cds__results">${items}</ul>
    `;

    this.inner.querySelector('[data-cds-change]')?.addEventListener('click', () => {
      sessionStorage.removeItem('customerPostcode');
      sessionStorage.removeItem('customerPostcodeLabel');
      sessionStorage.removeItem('customerCountry');
      sessionStorage.removeItem('customerProvince');
      sessionStorage.removeItem('customerConfigLocale');
      sessionStorage.removeItem('customerCoords');
      this.hidden = true;
      document.dispatchEvent(new CustomEvent('pss:coords-changed', { detail: null }));
    });
  }

  renderItem(name, price, subtext, badge) {
    let html = `<li class="cds__item">`;
    html += `<div class="cds__item-row">`;
    html += `<div class="cds__item-left"><span class="cds__dot" aria-hidden="true"></span><span class="cds__name">${this.escapeHtml(name)}</span></div>`;
    if (price) html += `<span class="cds__price">${price}</span>`;
    html += `</div>`;
    if (subtext || badge) {
      html += `<div class="cds__item-row cds__item-row--sub">`;
      if (subtext) html += `<span class="cds__subtext">${this.escapeHtml(subtext)}</span>`;
      if (badge) html += `<span class="cds__badge">${this.escapeHtml(badge)}</span>`;
      html += `</div>`;
    }
    html += `</li>`;
    return html;
  }

  renderStoreRow({ store, distance }) {
    const name = store.location.name;
    const distText = distance != null ? `${distance.toFixed(1)}KM Away` : '';
    return `
      <div class="cds__store">
        <span class="cds__dot" aria-hidden="true"></span>
        <span class="cds__store-name">${this.escapeHtml(name)}</span>
        ${distText ? `<span class="cds__store-dist">${distText}</span>` : ''}
      </div>
    `;
  }

  isSameDayRate(name) {
    if (!name) return false;
    const n = name.toLowerCase();
    return n.includes('uber') || n.includes('same day') || n.includes('same-day') || n.includes('asap');
  }

  getNZHour() {
    return parseInt(
      new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', hour: 'numeric', hour12: false }).format(new Date()),
      10
    );
  }

  findNearestStore(stores, coords) {
    const inStock = stores.filter((s) => s.available);
    if (!inStock.length) return null;
    if (!coords) return { store: inStock[0], distance: null };

    let nearest = null;
    let minDist = Infinity;
    for (const s of inStock) {
      const { latitude, longitude } = s.location.address;
      if (latitude == null || longitude == null) continue;
      const d = this.haversineKm(coords.lat, coords.lng, latitude, longitude);
      if (d < minDist) { minDist = d; nearest = s; }
    }
    return nearest ? { store: nearest, distance: minDist } : { store: inStock[0], distance: null };
  }

  haversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  async fetchBespokeRates(zip, country) {
    const token = window.pss?.token;
    if (!token) return [];

    const variantSku = this.dataset.variantSku || '';
    const variantPrice = this.dataset.variantPrice || '0';
    const productVendor = this.dataset.productVendor || '';
    const productId = this.dataset.productId || '';
    const variantId = this.dataset.variantId || '';

    const province = sessionStorage.getItem('customerProvince') || '';

    try {
      const res = await fetch('https://parcel-intelligence.com/cs/api_ex.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          HTTP_X_STORE_NAME: Shopify.shop,
          HTTP_X_STORE_TOKEN: token,
        },
        body: JSON.stringify({
          origin: { country: '', postal_code: '', province: '', city: '', name: '', address1: '', address2: '', address3: '', phone: '', fax: '', address_type: '', company_name: '' },
          destination: { country, postal_code: zip, province, city: '', name: '', address1: '', address2: '', address3: '', phone: '', fax: '', address_type: '', company_name: '' },
          items: [{
            sku: variantSku,
            quantity: 1,
            price: variantPrice,
            vendor: productVendor,
            requires_shipping: true,
            taxable: true,
            fulfillment_service: 'manual',
            properties: '',
            product_id: productId,
            variant_id: variantId,
          }],
          currency: Shopify.shop?.currency || 'NZD',
        }),
      });

      if (!res.ok) return [];
      const text = await res.text();
      const lines = text.trim().split('\n');
      const data = JSON.parse(lines[lines.length - 1]);

      return (data.rates || []).filter((r) => {
        const code = r?.service_code;
        if (!code) return false;
        if (code.startsWith('DONOTSELECT')) return false;
        if (code.startsWith('PICKUP_')) return false;
        return true;
      });
    } catch {
      return [];
    }
  }

  async fetchStoreAvailability(variantId) {
    const token = window.pss?.storefrontToken;
    if (!token) return [];

    const gid = `gid://shopify/ProductVariant/${variantId}`;
    const query = `{
      node(id: "${gid}") {
        ... on ProductVariant {
          storeAvailability(first: 20) {
            edges {
              node {
                available
                pickUpTime
                location { name address { formatted latitude longitude } }
              }
            }
          }
        }
      }
    }`;

    try {
      const res = await fetch('/api/2026-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query }),
      });
      const data = await res.json();
      return data?.data?.node?.storeAvailability?.edges?.map((e) => e.node) ?? [];
    } catch {
      return [];
    }
  }

  formatMoney(total, format, currency) {
    if (total == null) return '';
    const amount = (parseFloat(total) / 100).toFixed(2).replace(/\.00$/, '');
    let str = (format || '${{amount}}').replace(/\{\{[\s]*amount[\s]*\}\}/g, amount);
    if (currency) str += ` ${currency}`;
    return str;
  }

  escapeHtml(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('cart-delivery-summary')) {
  customElements.define('cart-delivery-summary', CartDeliverySummary);
}
